import { useEffect, useState, useRef, useMemo } from 'react';

import GeoComplySingleton from '~/utils/geocomply';

import { useLocation } from './useLocation';

interface OptimizedLocationState {
  locationState: string | undefined;
  locationCountry: string | undefined;
  locationStatus: string | undefined;
  locationErrors: unknown[] | undefined;
  isLocationReady: boolean;
  hasLocationToken: boolean;
}

export const useOptimizedLocation = (): OptimizedLocationState => {
  const location = useLocation();
  const [isLocationReady, setIsLocationReady] = useState(false);
  const [hasLocationToken, setHasLocationToken] = useState(false);
  const previousLocationRef = useRef<string | undefined>();
  const stableLocationRef = useRef<OptimizedLocationState | null>(null);

  useEffect(() => {
    const checkStoredToken = () => {
      try {
        const tokenData = GeoComplySingleton.getLocationTokenData();
        const hasToken = !!tokenData;
        setHasLocationToken(hasToken);
        setIsLocationReady(true);

        if (hasToken) {
          stableLocationRef.current = {
            locationState: tokenData.state,
            locationCountry: tokenData.country,
            locationStatus: tokenData.status?.toString(),
            locationErrors: tokenData.errors,
            isLocationReady: true,
            hasLocationToken: true,
          };
        }
      } catch (error) {
        setHasLocationToken(false);
        setIsLocationReady(true);
      }
    };

    checkStoredToken();
  }, []);

  useEffect(() => {
    const hasLocationChanged = previousLocationRef.current !== location.locationState;

    if (hasLocationChanged || !stableLocationRef.current) {
      const newState: OptimizedLocationState = {
        locationState: location.locationState,
        locationCountry: location.locationCountry,
        locationStatus: location.locationStatus?.toString(),
        locationErrors: location.locationErrors,
        isLocationReady: true,
        hasLocationToken: !!location.locationState,
      };

      stableLocationRef.current = newState;
      setHasLocationToken(!!location.locationState);
      previousLocationRef.current = location.locationState;
    }
  }, [
    location.locationState,
    location.locationCountry,
    location.locationStatus,
    location.locationErrors,
  ]);

  return useMemo(() => {
    if (stableLocationRef.current) {
      return stableLocationRef.current;
    }

    return {
      locationState: location.locationState,
      locationCountry: location.locationCountry,
      locationStatus: location.locationStatus?.toString(),
      locationErrors: location.locationErrors,
      isLocationReady,
      hasLocationToken,
    };
  }, [location, isLocationReady, hasLocationToken]);
};
